/* eslint-disable object-shorthand */
import {
	type BoundActions,
	createActionsHook,
	createStateHook,
	createStore,
	type HookActionsFunction,
	type HookStateFunction,
	type Store,
	type StoreActionApi,
} from 'react-sweet-state';

import type { ChatNewPayload } from '@atlaskit/rovo-triggers/types';

import { type ConversationAction } from '@atlassian/rovo-conversation-actions';

export type AgentMentionContext = Exclude<NonNullable<ChatNewPayload['data']['prompt']>, string>;

export type EditorActionsContext = {
	reference: string;
	onRemove: () => void;
	sourceId: string;
};

export type CopiedPageState = {
	contentId: string;
	actions: ConversationAction[];
};

export type EditorRovoButtonActionsState = {
	context?: EditorActionsContext;
	actions: ConversationAction[];
	/** Content id the copy-with-rovo experiment saw `?copiedPage` for. The only copy signal — the
	 * editor's smart-button plugin also writes `actions` with no selection context. */
	copiedPageContentId: string | null;
	/** Copy-with-rovo's actions, held apart from `actions` so neither side clobbers the other. */
	copiedPageActions: ConversationAction[];
	/** Raw Atlassian Account ID (AAID) from the mention node. */
	agentMentionId: string | null;
	/** Counter incremented on each new agent mention insertion. */
	agentMentionInsertionCount: number;
	/** Resolved Agent Studio UUID for the mentioned agent. */
	resolvedAgentMentionId: string | null;
	/** ADF content of the block containing the mentioned agent. */
	agentMentionContext: AgentMentionContext | null;
	/** Plain-text fallback prompt from the block containing the mentioned agent. */
	agentMentionPrompt: string | null;
	/** Display name of the mentioned agent. */
	agentMentionName: string | null;
	/** ProseMirror node type name of the block (e.g. 'taskItem', 'paragraph'). */
	agentMentionParentNodeType: string | null;
	/**
	 * Document-instance `localId` of the invoking mention node, forwarded to the agent-run-state
	 * bridge so live run-state keys back to the node. Optional so existing `setAgentMentionState`
	 * callers/tests that predate this field keep compiling.
	 */
	agentMentionNodeLocalId?: string | null;
};

type EditorRovoButtonAgentMentionStateKey =
	| 'agentMentionId'
	| 'agentMentionInsertionCount'
	| 'agentMentionContext'
	| 'agentMentionPrompt'
	| 'agentMentionName'
	| 'agentMentionParentNodeType'
	| 'agentMentionNodeLocalId';

export type EditorRovoButtonAgentMentionState = Pick<
	EditorRovoButtonActionsState,
	EditorRovoButtonAgentMentionStateKey
>;

type EditorRovoButtonActions = {
	setActions: (
		actions: ConversationAction[],
	) => ({ setState }: StoreActionApi<EditorRovoButtonActionsState>) => void;
	setContext: (
		context: EditorActionsContext | undefined,
	) => ({ setState }: StoreActionApi<EditorRovoButtonActionsState>) => void;
	removeContext: () => ({ setState }: StoreActionApi<EditorRovoButtonActionsState>) => void;
	removeActions: () => ({ setState }: StoreActionApi<EditorRovoButtonActionsState>) => void;
	setCopiedPageState: (
		state: CopiedPageState,
	) => ({ setState }: StoreActionApi<EditorRovoButtonActionsState>) => void;
	clearCopiedPageState: () => ({ setState }: StoreActionApi<EditorRovoButtonActionsState>) => void;
	setAgentMentionState: (
		state: EditorRovoButtonAgentMentionState,
	) => ({ setState, getState }: StoreActionApi<EditorRovoButtonActionsState>) => void;
	setResolvedAgentMentionId: (
		resolvedAgentMentionId: string | null,
	) => ({ setState }: StoreActionApi<EditorRovoButtonActionsState>) => void;
};

export const initialState: EditorRovoButtonActionsState = {
	actions: [],
	copiedPageContentId: null,
	copiedPageActions: [],
	agentMentionId: null,
	agentMentionInsertionCount: 0,
	resolvedAgentMentionId: null,
	agentMentionContext: null,
	agentMentionPrompt: null,
	agentMentionName: null,
	agentMentionParentNodeType: null,
	agentMentionNodeLocalId: null,
};

export const actions = {
	setActions:
		(actions: ConversationAction[]) =>
		({ setState }: StoreActionApi<EditorRovoButtonActionsState>): void => {
			setState({ actions });
		},
	setContext:
		(context: EditorActionsContext | undefined) =>
		({ setState }: StoreActionApi<EditorRovoButtonActionsState>): void => {
			setState({ context: context });
		},
	removeContext:
		() =>
		({ setState }: StoreActionApi<EditorRovoButtonActionsState>): void => {
			setState({ context: undefined });
		},
	removeActions:
		() =>
		({ setState }: StoreActionApi<EditorRovoButtonActionsState>): void => {
			setState({ actions: [] });
		},
	setCopiedPageState:
		({ contentId, actions: copiedPageActions }: CopiedPageState) =>
		({ setState }: StoreActionApi<EditorRovoButtonActionsState>): void => {
			setState({ copiedPageContentId: contentId, copiedPageActions });
		},
	clearCopiedPageState:
		() =>
		({ setState }: StoreActionApi<EditorRovoButtonActionsState>): void => {
			setState({ copiedPageContentId: null, copiedPageActions: [] });
		},
	setAgentMentionState:
		(state: EditorRovoButtonAgentMentionState) =>
		({ setState, getState }: StoreActionApi<EditorRovoButtonActionsState>): void => {
			// Only reset resolvedAgentMentionId when the agent itself changes — not when
			// agentMentionContext updates (user typing after the mention) — to prevent the nudge
			// from flashing on every keystroke while the resolved UUID is already available.
			const current = getState();
			// Reset resolvedAgentMentionId when the agent changes OR when a new insertion
			// is made for the same agent — the insertion count increase means a new fetch
			// is about to start, and the old resolved UUID should not persist if it fails.
			const shouldResetResolved =
				state.agentMentionId !== current.agentMentionId ||
				state.agentMentionInsertionCount !== current.agentMentionInsertionCount;
			setState({
				...state,
				...(shouldResetResolved ? { resolvedAgentMentionId: null } : {}),
			});
		},
	setResolvedAgentMentionId:
		(resolvedAgentMentionId: string | null) =>
		({ setState }: StoreActionApi<EditorRovoButtonActionsState>): void => {
			setState({ resolvedAgentMentionId });
		},
};

export const EditorRovoButtonActionsStore: Store<
	EditorRovoButtonActionsState,
	EditorRovoButtonActions
> = createStore({
	initialState,
	actions,
	name: 'EditorRovoButtonActionsStore',
});

export const useEditorRovoButtonActions: HookActionsFunction<
	BoundActions<EditorRovoButtonActionsState, EditorRovoButtonActions>
> = createActionsHook(EditorRovoButtonActionsStore);
export const useEditorRovoButtonActionsActions: HookStateFunction<ConversationAction[], void> =
	createStateHook(EditorRovoButtonActionsStore, {
		selector: (state: EditorRovoButtonActionsState) => state.actions,
	});
export const useEditorRovoButtonActionsContext: HookStateFunction<
	EditorActionsContext | undefined,
	void
> = createStateHook(EditorRovoButtonActionsStore, {
	selector: (state: EditorRovoButtonActionsState) => state.context,
});
export const useEditorRovoButtonCopiedPageContentId: HookStateFunction<string | null, void> =
	createStateHook(EditorRovoButtonActionsStore, {
		selector: (state: EditorRovoButtonActionsState) => state.copiedPageContentId,
	});
export const useEditorRovoButtonCopiedPageActions: HookStateFunction<ConversationAction[], void> =
	createStateHook(EditorRovoButtonActionsStore, {
		selector: (state: EditorRovoButtonActionsState) => state.copiedPageActions,
	});
export const useEditorRovoButtonAgentMentionId: HookStateFunction<string | null, void> =
	createStateHook(EditorRovoButtonActionsStore, {
		selector: (state: EditorRovoButtonActionsState) => state.agentMentionId,
	});
export const useEditorRovoButtonAgentMentionInsertionCount: HookStateFunction<number, void> =
	createStateHook(EditorRovoButtonActionsStore, {
		selector: (state: EditorRovoButtonActionsState) => state.agentMentionInsertionCount,
	});
export const useEditorRovoButtonResolvedAgentMentionId: HookStateFunction<string | null, void> =
	createStateHook(EditorRovoButtonActionsStore, {
		selector: (state: EditorRovoButtonActionsState) => state.resolvedAgentMentionId,
	});
export const useEditorRovoButtonAgentMentionContext: HookStateFunction<
	AgentMentionContext | null,
	void
> = createStateHook(EditorRovoButtonActionsStore, {
	selector: (state: EditorRovoButtonActionsState) => state.agentMentionContext,
});
export const useEditorRovoButtonAgentMentionPrompt: HookStateFunction<string | null, void> =
	createStateHook(EditorRovoButtonActionsStore, {
		selector: (state: EditorRovoButtonActionsState) => state.agentMentionPrompt,
	});
export const useEditorRovoButtonAgentMentionName: HookStateFunction<string | null, void> =
	createStateHook(EditorRovoButtonActionsStore, {
		selector: (state: EditorRovoButtonActionsState) => state.agentMentionName,
	});
export const useEditorRovoButtonAgentMentionParentNodeType: HookStateFunction<string | null, void> =
	createStateHook(EditorRovoButtonActionsStore, {
		selector: (state: EditorRovoButtonActionsState) => state.agentMentionParentNodeType,
	});
export const useEditorRovoButtonAgentMentionNodeLocalId: HookStateFunction<string | null, void> =
	createStateHook(EditorRovoButtonActionsStore, {
		selector: (state: EditorRovoButtonActionsState) => state.agentMentionNodeLocalId ?? null,
	});
